import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './MarketingAnalyticsPage.css';

function toMillisSafe(value) {
  if (!value) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object') {
    const seconds = value.seconds ?? value._seconds;
    const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      return (seconds * 1000) + Math.floor(Number(nanos || 0) / 1e6);
    }
  }
  return null;
}

function formatDateTime(value) {
  const ms = toMillisSafe(value);
  if (!ms) return '—';
  return new Date(ms).toLocaleString();
}

function formatInteger(value) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatPct(num, den) {
  if (!den) return '0.0%';
  return `${((Number(num || 0) / Number(den || 0)) * 100).toFixed(1)}%`;
}

function buildDateQuery(startDate, endDate, actionDate) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (actionDate) params.set('actionDate', actionDate);
  params.set('limit', '100');
  return params.toString();
}

export default function MarketingAnalyticsPage() {
  const { currentUser } = useAuth();
  const [report, setReport] = useState({
    latestAvailableDate: '',
    startDate: '',
    endDate: '',
    actionDate: '',
    summary: null,
    campaigns: [],
    paths: [],
    actions: [],
  });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    actionDate: '',
  });
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [bootstrapped, setBootstrapped] = useState(false);

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };
    return fetch(resolvedUrl, { ...options, headers });
  }, [currentUser]);

  const loadLastRun = useCallback(async () => {
    const response = await authedFetch('/api/v1/marketing-analytics/last-run');
    const data = await response.json();
    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || 'Failed to load marketing analytics run status.');
    }
    setLastRun(data?.run || null);
  }, [authedFetch]);

  const loadReport = useCallback(async (nextFilters = {}) => {
    setLoading(true);
    setError('');
    try {
      const query = buildDateQuery(nextFilters.startDate, nextFilters.endDate, nextFilters.actionDate);
      const response = await authedFetch(`/api/v1/marketing-analytics/report?${query}`);
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load marketing analytics.');
      }
      setReport({
        latestAvailableDate: data?.latestAvailableDate || '',
        startDate: data?.startDate || '',
        endDate: data?.endDate || '',
        actionDate: data?.actionDate || '',
        summary: data?.summary || null,
        campaigns: Array.isArray(data?.campaigns) ? data.campaigns : [],
        paths: Array.isArray(data?.paths) ? data.paths : [],
        actions: Array.isArray(data?.actions) ? data.actions : [],
      });
      setFilters((prev) => ({
        startDate: nextFilters.startDate || data?.startDate || prev.startDate,
        endDate: nextFilters.endDate || data?.endDate || prev.endDate,
        actionDate: nextFilters.actionDate || data?.actionDate || prev.actionDate,
      }));
      setBootstrapped(true);
    } catch (err) {
      setError(err.message || 'Failed to load marketing analytics.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadReport({ startDate: '', endDate: '', actionDate: '' });
    loadLastRun().catch((err) => setError(err.message || 'Failed to load last run.'));
  }, [loadReport, loadLastRun]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleRefresh = useCallback(async () => {
    setMessage('');
    await Promise.all([
      loadReport({
        startDate: filters.startDate,
        endDate: filters.endDate,
        actionDate: filters.actionDate,
      }),
      loadLastRun().catch((err) => setError(err.message || 'Failed to load last run.')),
    ]);
  }, [filters, loadLastRun, loadReport]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError('');
    setMessage('');
    try {
      const targetDate = String(filters.actionDate || report.latestAvailableDate || '').trim();
      const response = await authedFetch('/api/v1/marketing-analytics/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetDate }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to sync marketing analytics.');
      }
      setMessage(`Marketing analytics synced for ${data?.targetDate || targetDate || 'selected date'}.`);
      await handleRefresh();
    } catch (err) {
      setError(err.message || 'Failed to sync marketing analytics.');
    } finally {
      setSyncing(false);
    }
  }, [authedFetch, filters.actionDate, handleRefresh, report.latestAvailableDate]);

  const actionCounts = useMemo(() => {
    const counts = { scale: 0, assist_only: 0, hold: 0, reduce: 0, pause: 0 };
    report.actions.forEach((row) => {
      const key = String(row.actionFlag || '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1;
    });
    return counts;
  }, [report.actions]);

  return (
    <div className="marketing-analytics-page">
      <div className="marketing-analytics-hero">
        <div>
          <h1>Marketing Analytics</h1>
          <p>Admin-only attribution, funnel, and budget action view built from GA4 marts.</p>
        </div>
        <div className="marketing-analytics-actions">
          <button type="button" onClick={handleRefresh} disabled={loading || syncing}>Refresh</button>
          <button type="button" className="primary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync Selected Date'}
          </button>
        </div>
      </div>

      <div className="marketing-analytics-toolbar">
        <label>
          Range Start
          <input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
        </label>
        <label>
          Range End
          <input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
        </label>
        <label>
          Action Date
          <input type="date" value={filters.actionDate} onChange={(e) => handleFilterChange('actionDate', e.target.value)} />
        </label>
        <div className="marketing-analytics-meta">
          <span>Latest data: <strong>{report.latestAvailableDate || '—'}</strong></span>
          <span>Last sync: <strong>{lastRun ? formatDateTime(lastRun.endedAt || lastRun.startedAt) : '—'}</strong></span>
          <span>Mode: <strong>{bootstrapped ? 'manual refresh only' : 'loading'}</strong></span>
        </div>
      </div>

      {error ? <div className="marketing-alert error">{error}</div> : null}
      {message ? <div className="marketing-alert success">{message}</div> : null}

      <section className="marketing-grid">
        <article className="marketing-card">
          <span className="label">Sessions</span>
          <strong>{formatInteger(report.summary?.sessions)}</strong>
          <small>{formatInteger(report.summary?.users)} users</small>
        </article>
        <article className="marketing-card">
          <span className="label">View Item</span>
          <strong>{formatInteger(report.summary?.viewItemEvents)}</strong>
          <small>{formatPct(report.summary?.viewItemEvents, report.summary?.sessions)} of sessions</small>
        </article>
        <article className="marketing-card">
          <span className="label">Add To Cart</span>
          <strong>{formatInteger(report.summary?.addToCartEvents)}</strong>
          <small>{formatPct(report.summary?.addToCartEvents, report.summary?.sessions)} ATC rate</small>
        </article>
        <article className="marketing-card">
          <span className="label">Purchases</span>
          <strong>{formatInteger(report.summary?.purchaseTransactions)}</strong>
          <small>{formatPct(report.summary?.purchaseTransactions, report.summary?.sessions)} conversion rate</small>
        </article>
        <article className="marketing-card">
          <span className="label">Revenue</span>
          <strong>{formatCurrency(report.summary?.purchaseRevenue)}</strong>
          <small>{formatInteger(report.summary?.campaignRows)} campaign rows</small>
        </article>
      </section>

      <section className="marketing-section">
        <div className="marketing-section-header">
          <div>
            <h2>Budget Actions</h2>
            <p>Decision snapshot for {report.actionDate || '—'}.</p>
          </div>
          <div className="marketing-action-pills">
            <span className="pill scale">Scale {actionCounts.scale}</span>
            <span className="pill assist">Assist {actionCounts.assist_only}</span>
            <span className="pill hold">Hold {actionCounts.hold}</span>
            <span className="pill reduce">Reduce {actionCounts.reduce}</span>
            <span className="pill pause">Pause {actionCounts.pause}</span>
          </div>
        </div>
        <div className="marketing-table-wrap">
          <table className="marketing-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Campaign</th>
                <th>Source / Medium</th>
                <th>Sessions</th>
                <th>ATC</th>
                <th>Purchases</th>
                <th>Assist</th>
                <th>Revenue</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {report.actions.map((row) => (
                <tr key={`action-${row.source}-${row.medium}-${row.campaign}-${row.content}-${row.term}`}>
                  <td><span className={`pill ${String(row.actionFlag || '').toLowerCase()}`}>{row.actionFlag}</span></td>
                  <td>
                    <div className="campaign-cell">
                      <strong>{row.campaign}</strong>
                      <span>{row.content !== '(not set)' ? row.content : row.term}</span>
                    </div>
                  </td>
                  <td>{row.source} / {row.medium}</td>
                  <td>{formatInteger(row.sessions)}</td>
                  <td>{formatInteger(row.addToCartEvents)}</td>
                  <td>{formatInteger(row.purchaseTransactions)}</td>
                  <td>{formatInteger(row.assistPurchases)}</td>
                  <td>{formatCurrency(row.purchaseRevenue)}</td>
                  <td>{row.actionReason}</td>
                </tr>
              ))}
              {!report.actions.length && !loading ? (
                <tr><td colSpan="9" className="empty-cell">No action rows found for the selected date.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-section-header">
          <div>
            <h2>Campaign Funnel</h2>
            <p>Aggregated from {report.startDate || '—'} to {report.endDate || '—'}.</p>
          </div>
        </div>
        <div className="marketing-table-wrap">
          <table className="marketing-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Source / Medium</th>
                <th>Sessions</th>
                <th>Engaged</th>
                <th>Views</th>
                <th>ATC</th>
                <th>Checkout</th>
                <th>Purchases</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {report.campaigns.map((row) => (
                <tr key={`campaign-${row.source}-${row.medium}-${row.campaign}-${row.content}-${row.term}`}>
                  <td>
                    <div className="campaign-cell">
                      <strong>{row.campaign}</strong>
                      <span>{row.content !== '(not set)' ? row.content : row.term}</span>
                    </div>
                  </td>
                  <td>{row.source} / {row.medium}</td>
                  <td>{formatInteger(row.sessions)}</td>
                  <td>{formatInteger(row.engagedSessions)}</td>
                  <td>{formatInteger(row.viewItemEvents)}</td>
                  <td>{formatInteger(row.addToCartEvents)}</td>
                  <td>{formatInteger(row.beginCheckoutEvents)}</td>
                  <td>{formatInteger(row.purchaseTransactions)}</td>
                  <td>{formatCurrency(row.purchaseRevenue)}</td>
                </tr>
              ))}
              {!report.campaigns.length && !loading ? (
                <tr><td colSpan="9" className="empty-cell">No campaign rows found for the selected range.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-section-header">
          <div>
            <h2>First Touch To Last Touch</h2>
            <p>Use this to identify introducers, closers, and cross-touch conversion paths.</p>
          </div>
        </div>
        <div className="marketing-table-wrap">
          <table className="marketing-table">
            <thead>
              <tr>
                <th>First Touch</th>
                <th>Last Touch</th>
                <th>Purchases</th>
                <th>Cross Touch</th>
                <th>Same Touch</th>
                <th>Revenue</th>
                <th>Lag (hrs)</th>
              </tr>
            </thead>
            <tbody>
              {report.paths.map((row) => (
                <tr key={`path-${row.firstSource}-${row.firstMedium}-${row.firstCampaign}-${row.lastSource}-${row.lastMedium}-${row.lastCampaign}`}>
                  <td>{row.firstCampaign} <span className="subtle">({row.firstSource} / {row.firstMedium})</span></td>
                  <td>{row.lastCampaign} <span className="subtle">({row.lastSource} / {row.lastMedium})</span></td>
                  <td>{formatInteger(row.purchases)}</td>
                  <td>{formatInteger(row.crossTouchPurchases)}</td>
                  <td>{formatInteger(row.sameTouchPurchases)}</td>
                  <td>{formatCurrency(row.purchaseRevenue)}</td>
                  <td>{Number(row.avgConversionLagHours || 0).toFixed(1)}</td>
                </tr>
              ))}
              {!report.paths.length && !loading ? (
                <tr><td colSpan="7" className="empty-cell">No conversion path rows found for the selected range.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
